import { Boundaries } from "@planzone/core";
import { Identifiable, Status } from "@planzone/dto";
import { events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = [];

function sortStatusesByPercentageOfCompletion(
  statuses: Identifiable<Status>[],
): Identifiable<Status>[] {
  return [...statuses].sort((current, next) =>
    (current.percentComplete || 0) < (next.percentComplete || 0) ? -1 : 1,
  );
}

function handleRequest(
  state: State,
  project: Boundaries["retrieveTaskStatuses"]["request"],
): State {
  const index = state.findIndex((res) => project.uuid === res.project.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToPending() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        { project, request: setRequestStateToPending(), statuses: [] },
      ];
}

function handleSuccess(
  state: State,
  { project, statuses }: Boundaries["retrieveTaskStatuses"]["success"],
): State {
  const index = state.findIndex((res) => project.uuid === res.project.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          request: setRequestStateToSuccess(),
          statuses: sortStatusesByPercentageOfCompletion(statuses),
        },
        ...state.slice(index + 1),
      ]
    : [...state, { project, request: setRequestStateToSuccess(), statuses }];
}

function handleFailure(
  state: State,
  project: Boundaries["retrieveTaskStatuses"]["failure"],
): State {
  const index = state.findIndex((res) => project.uuid === res.project.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToFailure() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        { project, request: setRequestStateToFailure(), statuses: [] },
      ];
}

function handleStatusCreationRequest(
  state: State,
  { project, status }: Boundaries["createTaskStatus"]["request"],
) {
  return state.map((res) =>
    res.project.uuid === project.uuid
      ? {
          ...res,
          statuses: sortStatusesByPercentageOfCompletion([
            ...res.statuses,
            { ...status, uuid: status.label },
          ]),
        }
      : res,
  );
}

function handleStatusCreationSuccess(
  state: State,
  { project, status }: Boundaries["createTaskStatus"]["success"],
) {
  return state.map((res) =>
    res.project.uuid === project.uuid
      ? {
          ...res,
          statuses: sortStatusesByPercentageOfCompletion(
            res.statuses.map((s) => (s.label === status.label ? status : s)),
          ),
        }
      : res,
  );
}

function handleStatusCreationFailure(
  state: State,
  { project, status }: Boundaries["createTaskStatus"]["failure"],
) {
  return state.map((res) =>
    res.project.uuid === project.uuid
      ? {
          ...res,
          statuses: sortStatusesByPercentageOfCompletion(
            res.statuses.filter((s) => s.uuid !== status.label),
          ),
        }
      : res,
  );
}

function handleStatusEditionRequest(
  state: State,
  { status, nextStatus }: Boundaries["editTaskStatus"]["request"],
) {
  return state.map((res) => ({
    ...res,
    statuses: res.statuses.map((s) =>
      status.uuid === s.uuid ? { ...s, ...nextStatus } : s,
    ),
  }));
}

function handleStatusEditionSuccess(
  state: State,
  nextStatus: Boundaries["editTaskStatus"]["success"],
) {
  return state.map((res) => ({
    ...res,
    statuses: res.statuses.map((s) =>
      nextStatus.uuid === s.uuid ? nextStatus : s,
    ),
  }));
}

function handleStatusEditionFailure(
  state: State,
  { status }: Boundaries["editTaskStatus"]["failure"],
) {
  return state.map((res) => ({
    ...res,
    statuses: res.statuses.map((s) =>
      status.uuid === s.uuid ? { ...s, ...status } : s,
    ),
  }));
}

const reduceState = resetState(
  initialState,
  createStateReducer(initialState, {
    [events.retrieveTaskStatusesRequested]: handleRequest,
    [events.retrieveTaskStatusesSucceeded]: handleSuccess,
    [events.retrieveTaskStatusesFailed]: handleFailure,
    [events.createTaskStatusRequested]: handleStatusCreationRequest,
    [events.createTaskStatusSucceeded]: handleStatusCreationSuccess,
    [events.createTaskStatusFailed]: handleStatusCreationFailure,
    [events.editTaskStatusRequested]: handleStatusEditionRequest,
    [events.editTaskStatusSucceeded]: handleStatusEditionSuccess,
    [events.editTaskStatusFailed]: handleStatusEditionFailure,
  }),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
